import React, { useState } from 'react';
import { Eye, ShoppingCart, Package, ChevronDown, ChevronUp } from 'lucide-react';
import './index.css';

interface CustomItem {
  displayName: string;
  isAddOn: boolean;
  menuItemId: string;
  productId: string;
  quantity: number;
  tinyId: number;
  unitPrice: number;
  skuId?: string;
}

interface CartAllocationItem {
  productName?: string;
  displayName?: string;
  itemId?: string;
  productId?: string;
  skuId?: string;
  price?: number;
  tier?: string;
  quantity: number;
  subtotal?: number;
  itemType?: string;
  customItems?: CustomItem[];
}

export interface CartAllocationEntry {
  cartId?: string;
  cartName?: string;
  allocations?: CartAllocationItem[];
  totalAmount?: number;
  totalQuantity?: number;
}

interface CartAllocationsViewProps {
  cartAllocations: CartAllocationEntry[];
  onViewCustomItems: (productName: string, items: CustomItem[]) => void;
}

const CartAllocationsView: React.FC<CartAllocationsViewProps> = ({
  cartAllocations,
  onViewCustomItems,
}) => {
  const [expandedCarts, setExpandedCarts] = useState<Record<number, boolean>>({});

  const toggleCart = (idx: number) => {
    setExpandedCarts(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  if (!cartAllocations || cartAllocations.length === 0) {
    return (
      <div className="ca-empty">
        <ShoppingCart size={16} />
        <span>No cart allocations found</span>
      </div>
    );
  }

  return (
    <div className="ca-container">
      {/* Section title */}
      <div className="ca-title">
        <ShoppingCart size={14} />
        <span>Cart Allocations</span>
        <span className="ca-count-badge">{cartAllocations.length} cart(s)</span>
      </div>

      {/* Cart list */}
      <div className="ca-list">
        {cartAllocations.map((cart, idx) => {
          const isExpanded = expandedCarts[idx] ?? false;
          const allocations = cart.allocations || [];
          const totalQty = cart.totalQuantity ?? allocations.reduce((s, a) => s + (a.quantity || 0), 0);
          const totalAmt = cart.totalAmount ?? allocations.reduce((s, a) => s + (a.subtotal || 0), 0);

          return (
            <div key={idx} className="ca-entry">
              {/* Cart header — clickable to expand */}
              <button
                className={`ca-entry-header ${isExpanded ? 'ca-entry-header--expanded' : ''}`}
                onClick={() => toggleCart(idx)}
              >
                <div className="ca-entry-left">
                  <div className="ca-icon-wrap">
                    <ShoppingCart size={13} />
                  </div>
                  <div className="ca-entry-info">
                    <span className="ca-entry-name">{cart.cartName || `Cart ${idx + 1}`}</span>
                  
                  </div>
                </div>

                <div className="ca-entry-right">
                  <span className="ca-entry-stat">
                    <Package size={11} />
                    {allocations.length} product(s)
                  </span>
                  <span className="ca-entry-stat">{totalQty} units</span>
                  {totalAmt > 0 && (
                    <span className="ca-entry-amount">Rs. {totalAmt.toLocaleString()}</span>
                  )}
                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </button>

              {/* Expanded product rows */}
              {isExpanded && (
                <div className="ca-products-list">
                  {allocations.length === 0 ? (
                    <div className="ca-products-empty">No products allocated</div>
                  ) : (
                    allocations.map((alloc, aIdx) => {
                      const name = alloc.productName || alloc.displayName || 'Unknown Product';
                      const isCustomMenu =
                        alloc.itemType === 'CustomMenu' &&
                        alloc.customItems &&
                        alloc.customItems.length > 0;

                      return (
                        <div key={aIdx} className="ca-product-row">
                          <div className="ca-product-left">
                            {alloc.tier && (
                              <span className={`tier-badge ${alloc.tier.toLowerCase()}`}>
                                {alloc.tier}
                              </span>
                            )}
                            <div className="ca-product-details">
                              <span className="ca-product-name">{name}</span>
                              {alloc.skuId && (
                                <span className="ca-product-sku">{alloc.skuId}</span>
                              )}
                            </div>
                          </div>

                          <div className="ca-product-right">
                            {alloc.price != null && (
                              <span className="ca-product-price">
                                Rs. {alloc.price.toLocaleString()}
                              </span>
                            )}
                            <span className="ca-product-qty">×{alloc.quantity}</span>
                            {alloc.subtotal != null && alloc.subtotal > 0 && (
                              <span className="ca-product-subtotal">
                                Rs. {alloc.subtotal.toLocaleString()}
                              </span>
                            )}
                            {isCustomMenu && (
                              <button
                                className="ca-custom-view-btn"
                                onClick={() => onViewCustomItems(name, alloc.customItems!)}
                                title="View custom menu items"
                              >
                                <Eye size={13} strokeWidth={2.2} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartAllocationsView;